// @ts-nocheck
import Vue, { configureCompat, vModelDynamic } from "vue";

// Central Vue 2 compat flags for legacy render functions, listeners, v-model and filters.
configureCompat({
	MODE: 2,
	RENDER_FUNCTION: true,
	INSTANCE_LISTENERS: true,
	COMPONENT_V_MODEL: true,
	FILTERS: true
});

const filters = {};
const originalFilter = typeof Vue.filter === "function" ? Vue.filter.bind(Vue) : null;

const numberCommas = (value) => {
	if (value && !Number.isNaN(value)) {
		return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
	}
	return value;
};

const shortNumber = (value) => {
	let number = "";
	let suffix = "";

	if (value < 1000) {
		return value;
	}
	if (value >= 1000 && value < 1000000) {
		number = (value / 1000).toFixed(1);
		suffix = "K";
	} else if (value >= 1000000 && value < 1000000000) {
		number = (value / 1000000).toFixed(1);
		suffix = "M";
	} else if (value >= 1000000000) {
		number = (value / 1000000000).toFixed(1);
		suffix = "B";
	}

	if (number.split(".")[1] === "0") {
		return number.split(".")[0] + suffix;
	}

	return number + suffix;
};

const decimalPlaces = (value, places = 2) => {
	if (value && !Number.isNaN(value)) {
		return value.toFixed(places);
	}
	return value;
};

const countSelected = (value) => {
	if (!value) return "";
	return value.filter((i) => i._selected).length + " / " + value.length;
};

const toCurrency = (value) => {
	if (typeof value !== "number") {
		return value;
	}
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
	}).format(value);
};

const capitalize = (value) => {
	if (!value) return "";
	return value.toString().charAt(0).toUpperCase() + value.toString().slice(1);
};

const removeTrailingZeros = (value, decimalPoint = 2, prefix = "") => {
	if (!value) return "0" + prefix;
	const num = parseFloat(value);
	if (isNaN(num)) return "" + prefix;

	return (
		(num.toString().replace(/\.?0+$/, "").length > num.toString().split(".")[0].length
			? num.toFixed(decimalPoint).replace(/\.?0+$/, "")
			: num.toFixed(0)) + prefix
	);
};

const legacyFilter = (name, fn) => {
	if (fn) {
		filters[name] = fn;
		if (originalFilter) originalFilter(name, fn);
		return fn;
	}
	return filters[name] || (originalFilter && originalFilter(name));
};

Object.assign(Vue, {
	filter: legacyFilter,
	set(target, key, value) {
		target[key] = value;
		return value;
	},
	delete(target, key) {
		delete target[key];
	},
});

legacyFilter("numberCommas", numberCommas);
legacyFilter("shortNumber", shortNumber);
legacyFilter("decimalPlaces", decimalPlaces);
legacyFilter("countSelected", countSelected);
legacyFilter("toCurrency", toCurrency);
legacyFilter("capitalize", capitalize);
legacyFilter("removeTrailingZeros", removeTrailingZeros);

// Legacy render functions (bootstrap-vue checkbox/radio) use `directives: [{ name: 'model' }]`
// for display sync only and update the value via their own `change` listener. Vue 3's model
// directive then has no assigner and throws "assign is not a function" on change.
function noop() {}

function withLegacyAssigner(hook) {
	return (el, binding, vnode, prevVNode) => {
		const props = vnode.props || (vnode.props = {});
		if (!props["onUpdate:modelValue"] && !props["onModelCompat:input"]) {
			props["onModelCompat:input"] = noop;
		}
		return vModelDynamic[hook](el, binding, vnode, prevVNode);
	};
}

const legacyModelDirective = {
	created: withLegacyAssigner("created"),
	mounted: withLegacyAssigner("mounted"),
	beforeUpdate: withLegacyAssigner("beforeUpdate"),
	updated: withLegacyAssigner("updated"),
};

export function installVue2Compat(app) {
	// Central Vue 2 compatibility layer for legacy filters and instance helpers.
	app.config.globalProperties.$filters = filters;
	app.config.globalProperties.$set = Vue.set;
	app.config.globalProperties.$delete = Vue.delete;
	// Replaces compat's `model` (vModelDynamic) used by legacy render functions; avoids the built-in name warning
	app._context.directives.model = legacyModelDirective;
}
